import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { LayoutDashboard, FileText, FolderTree, Image, Users, Mail, Search, ArrowRightLeft, Settings, LogOut, Tag, Menu as MenuIcon, Lightbulb, Layers, Calendar, MessageSquare, BookMarked } from "lucide-react";
import { useAuth } from "@/lib/auth";

const NAV = [
  { to: "/admin", label: "Genel Bakış", icon: LayoutDashboard, perm: "dashboard", end: true },
  { to: "/admin/ideas", label: "İçerik Havuzu", icon: Lightbulb, perm: "content.read" },
  { to: "/admin/articles", label: "İçerikler", icon: FileText, perm: "content.read" },
  { to: "/admin/categories", label: "Kategoriler", icon: FolderTree, perm: "content.read" },
  { to: "/admin/tags", label: "Etiketler", icon: Tag, perm: "content.read" },
  { to: "/admin/collections", label: "Koleksiyonlar", icon: Layers, perm: "content.read" },
  { to: "/admin/events", label: "Kapadokya Takvimi", icon: Calendar, perm: "content.read" },
  { to: "/admin/stories", label: "Capella Stories", icon: MessageSquare, perm: "content.read" },
  { to: "/admin/media", label: "Medya", icon: Image, perm: "media.read" },
  { to: "/admin/leads", label: "Lead'ler", icon: Mail, perm: "leads.read" },
  { to: "/admin/seo", label: "SEO", icon: Search, perm: "seo.read" },
  { to: "/admin/redirects", label: "Yönlendirmeler", icon: ArrowRightLeft, perm: "redirect.read" },
  { to: "/admin/menus", label: "Menüler", icon: MenuIcon, perm: "menu.read" },
  { to: "/admin/users", label: "Kullanıcılar", icon: Users, perm: "*" },
  { to: "/admin/settings", label: "Ayarlar", icon: Settings, perm: "*" },
];

function hasPerm(perms = [], required) {
  if (!required) return true;
  if (perms.includes("*")) return true;
  if (perms.includes(required)) return true;
  const group = required.split(".")[0] + ".*";
  return perms.includes(group);
}

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { document.body.classList.add("admin-body"); return () => document.body.classList.remove("admin-body"); }, []);

  // Auth gate
  if (user === null) return <div className="p-10 font-admin text-cc-muted">Yükleniyor…</div>;
  if (user === false) {
    // bounce to login
    if (!location.pathname.startsWith("/admin/login")) {
      navigate("/admin/login", { replace: true });
    }
    return null;
  }

  const perms = user.permissions || [];
  const visibleNav = NAV.filter((n) => hasPerm(perms, n.perm === "*" ? null : n.perm) || user.role === "super_admin");

  return (
    <div className="min-h-screen flex font-admin">
      <aside className="w-64 bg-admin-sidebar text-white flex flex-col py-6" data-testid="admin-sidebar">
        <div className="px-5 mb-8">
          <Link to="/admin" className="flex items-center gap-3" data-testid="admin-sidebar-logo">
            <img
              src="/media/img/capella-logo.png"
              alt="Capella Cave"
              className="h-16 w-auto"
            />
          </Link>
          <div className="text-[10px] tracking-overline uppercase text-white/50 mt-2">Yönetim Paneli</div>
        </div>
        <nav className="flex-1 px-2 space-y-0.5 overflow-y-auto no-scrollbar">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.end}
                data-testid={`admin-nav-${n.to.replace(/\//g, "-")}`}
                className={({ isActive }) => `admin-nav-item ${isActive ? "active" : ""}`}>
                <Icon size={16} /> <span>{n.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-5 pt-6 border-t border-white/10">
          <div className="text-xs text-white/70 mb-1">{user.name || user.email}</div>
          <div className="text-[10px] uppercase tracking-overline text-cc-sepia">{user.role}</div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} className="mt-4 flex items-center gap-2 text-xs text-white/60 hover:text-white" data-testid="admin-logout">
            <LogOut size={14} /> Çıkış yap
          </button>
        </div>
      </aside>

      <main className="flex-1 bg-admin-bg overflow-y-auto" data-testid="admin-main">
        <div className="p-8 md:p-10 max-w-7xl">{children}</div>
      </main>
    </div>
  );
}
